import { t } from '@lingui/core/macro';
import { FieldErrors, sameValue } from 'src/components/forms/useEntityEditor';
import { OrgUnitItem } from 'src/components/forms/lookups';
import { isBlank, isEmptyPhone, isValidEmail, isValidPhone } from 'src/components/forms/validators';
import { OrgUnitEditorMode } from 'src/util/orgUnitAccess';
import { OrgUnitTypeRecord, allowedParentTypeIds, typeBehaviour } from 'src/util/orgUnitTypes';
import { MAX_OTHER_CONTACTS, OrgContact, OrgContactInfo, contactRows } from './contacts';
import { OrgUnitRecord } from './orgUnitModel';

export type ValidateOrgUnitOptions = {
  mode: OrgUnitEditorMode;
  isNew: boolean;
  type: OrgUnitTypeRecord | null;
  parent: OrgUnitItem | null;
  /**
   * The unit as loaded. Format and address rules skip values it already had, so legacy data the
   * rules predate (an "ext. 12" phone, a missing address) never blocks saving other changes.
   */
  original?: OrgUnitRecord;
};

// The backend's SectorOrgUnitCodeValidator pattern.
const SECTOR_CODE = /^[A-Z]{2,3}$/;

const INSTITUTION_ADDRESS_FIELDS = ['address1', 'city', 'state', 'postalCode'];
const INSTITUTION_CREATE_FIELDS = ['entityIdentifier', 'licenceNumber'];

function requireValue(errors: FieldErrors, path: string, value: unknown) {
  if (isBlank(value)) errors[path] = t`This field is required.`;
}

export function validateOrgUnit(unit: OrgUnitRecord, options: ValidateOrgUnitOptions): FieldErrors {
  const errors: FieldErrors = {};
  if (options.mode === 'readonly') return errors;

  const legacy = options.isNew ? undefined : options.original;
  if (options.mode === 'full') validateGeneral(unit, options, errors);
  if (typeBehaviour(options.type?.name).isInstitution) validateContacts(unit.orgContactInfo, legacy?.orgContactInfo, errors);

  return errors;
}

function validateGeneral(unit: OrgUnitRecord, { isNew, type, parent, original }: ValidateOrgUnitOptions, errors: FieldErrors) {
  requireValue(errors, 'name', unit.name);
  if (unit.organizationalUnitTypeId == null) {
    errors.organizationalUnitTypeId = t`This field is required.`;
    return;
  }

  const behaviour = typeBehaviour(type?.name);
  validateParent(unit, { type, parent }, errors);

  if (behaviour.code === 'typed' && !SECTOR_CODE.test(String(unit.code ?? ''))) errors.code = t`Use 2 or 3 capital letters.`;

  if (behaviour.isInstitution) {
    INSTITUTION_ADDRESS_FIELDS.forEach(field => {
      if (isNew || !original || !sameValue(unit[field], original[field])) requireValue(errors, field, unit[field]);
    });
    if (isNew) INSTITUTION_CREATE_FIELDS.forEach(field => requireValue(errors, field, unit[field]));
  }
}

function validateParent(unit: OrgUnitRecord, { type, parent }: Pick<ValidateOrgUnitOptions, 'type' | 'parent'>, errors: FieldErrors) {
  const allowed = allowedParentTypeIds(type);
  if (allowed.length === 0) return;
  if (unit.parentId == null) {
    errors.parentId = t`This field is required.`;
    return;
  }
  // Unknown while the tree is still loading; the backend checks again on save.
  if (!parent) return;

  const parentTypeId = parent.organizationalUnitType?.id;
  if (parentTypeId != null && !allowed.includes(parentTypeId)) {
    errors.parentId = t`This type can't sit under the selected parent.`;
  } else if (typeBehaviour(type?.name).code === 'derived' && isBlank(parent.code)) {
    errors.parentId = t`Pick a parent with a code first.`;
  }
}

/** Every email and phone the loaded unit's contacts already had, whichever row they sit in now. */
function legacyContactValues(info: OrgContactInfo | undefined): { email: unknown[]; phone: unknown[] } {
  const contacts = info ? contactRows(info).map(row => row.contact) : [];
  return { email: contacts.map(contact => contact.email), phone: contacts.map(contact => contact.phone) };
}

function validateContacts(info: OrgContactInfo, original: OrgContactInfo | undefined, errors: FieldErrors) {
  const legacy = legacyContactValues(original);
  // Matched by value, not row: removing an "Other" row shifts the rows after it.
  const isNewValue = (field: 'email' | 'phone', value: unknown) => !legacy[field].some(existing => sameValue(existing, value));
  contactRows(info).forEach(row => validateContact(row.contact, row.path, isNewValue, errors));
  if (info.otherContacts.length > MAX_OTHER_CONTACTS) {
    errors['orgContactInfo.otherContacts'] = t`Add at most ${MAX_OTHER_CONTACTS} other contacts.`;
  }
}

function validateContact(contact: OrgContact, path: string, isNewValue: (field: 'email' | 'phone', value: unknown) => boolean, errors: FieldErrors) {
  if (!isBlank(contact.email) && isNewValue('email', contact.email) && !isValidEmail(contact.email)) {
    errors[`${path}.email`] = t`Enter a valid email address.`;
  }
  if (!isEmptyPhone(contact.phone) && isNewValue('phone', contact.phone) && !isValidPhone(String(contact.phone))) {
    errors[`${path}.phone`] = t`Enter a valid phone number.`;
  }
}
